import type { CCDialog } from '@app/components/dialog/cc-dialog.component'
import { logout } from '@app/shared/auth'
import { getPublicConfig } from '@app/shared/config'
import { dialog } from '@app/shared/dialog'
import { BehaviorSubject, debounceTime } from 'rxjs'

// absolute session timers
let absoluteTimer = 0
let absoluteWarningTimer = 0

// absolute session duration and warning
let absoluteDuration = 60 * 3 * 60 * 1000 // 3 hours
let absoluteWarningDuration = absoluteDuration - 5 * 60 * 1000 // 5 minutes before absolute

// inactivity timers
let inactivityTimer = 0
let inactivityWarningTimer = 0

// inactivity duration and warning
let inactivityDuration = 30 * 60 * 1000 // 30 minutes
let inactivityWarningDuration = inactivityDuration - 2 * 60 * 1000 // 2 minutes before inactivity
let inactivityWarningVisible = false

// cross tab channel
const channel = new BroadcastChannel('session-channel')
const $message = new BehaviorSubject<string>('')

channel.addEventListener('message', (event) => {
  const message = event.data
  if (message === 'reset') {
    document.querySelector<CCDialog>('cc-dialog#inactivity-dialog')?.hide('true')
    inactivityWarningVisible = false
    initInactivityTimers()
  }
  if (localStorage.getItem('absolute-session-seen')) {
    document.querySelector<CCDialog>('cc-dialog#max-session-dialog')?.hide('true')
  }
})

const initInactivityTimers = () => {
  clearTimeout(inactivityTimer)
  clearTimeout(inactivityWarningTimer)

  // Set the warning timer first.
  inactivityWarningTimer = setTimeout(onInactiveWarning, inactivityWarningDuration)

  // Set the inactivity timer.
  inactivityTimer = setTimeout(onInactive, inactivityDuration)
}

const broadcastMessage = (message: string) => () => $message.next(message)

const onInactiveWarning = async () => {
  console.log("Warning: You'll be logged out due to inactivity soon.")
  inactivityWarningVisible = true
  const reset = await dialog({
    id: 'inactivity-dialog',
    header: 'Still there?',
    message: 'You will be signed out due to inactivity.',
    closeText: 'Stay signed in',
    modal: true,
  })
  if (reset) {
    inactivityWarningVisible = false
    initInactivityTimers()
  }
}

const onInactive = async () => {
  console.log('Info: You have been logged out due to inactivity.')
  await logout()
}

const onAbsolute = async () => {
  console.log('Info: You have been logged out due to max session time.')
  await logout()
}

const onAbsoluteWarning = async () => {
  console.log("Warning: You'll be logged out due to max session time.")
  document.querySelector<CCDialog>('cc-dialog#inactivity-dialog')?.hide('true')

  let remainingSeconds = Math.floor((absoluteDuration - absoluteWarningDuration) / 1000)

  const updateTimer = () => {
    const display = document.querySelector('cc-dialog#max-session-dialog #count') || { textContent: '' }
    // If time is up, clear the timer and stop
    if (remainingSeconds < 0) {
      clearInterval(timerInterval)
      display.textContent = '00:00'
      return
    }

    // Calculate remaining minutes and seconds
    const minutes = Math.floor(remainingSeconds / 60)
    const seconds = remainingSeconds % 60

    // Format the time (adds a leading '0' if the number is less than 10)
    const formattedMinutes = minutes < 10 ? '0' + minutes : minutes
    const formattedSeconds = seconds < 10 ? '0' + seconds : seconds

    // Update the HTML display
    display.textContent = formattedMinutes + ':' + formattedSeconds

    // Decrease the total seconds
    remainingSeconds--
  }

  const timerInterval = setInterval(updateTimer, 1000)
  const reset = await dialog({
    id: 'max-session-dialog',
    header: 'Session Expiration',
    message: `
      Your session will end in <span id="count" style="min-width: 47px; display: inline-block; text-align: center;">${Math.floor(remainingSeconds / 60)}</span> minutes. <strong>Save your work now</strong> to avoid losing progress.
      <br/>
      <ul>
        <li>Log out and back in after saving, or</li>
        <li>Remain signed in and you will be automatically logged out when the session expires.</li>
      </ul>
    `,
    closeText: 'Ok',
    modal: true,
  })
  if (reset) {
    localStorage.setItem('absolute-session-seen', 'true')
  }
}

export const startInactivityDetection = async () => {
  const { inactivitySettings } = await getPublicConfig()

  if (inactivitySettings && !inactivitySettings.enableInactivityDetection) {
    return
  }

  let activityBroadcastThrottle = 1000

  if (inactivitySettings) {
    inactivityDuration = inactivitySettings.inactivityDurationMs
    inactivityWarningDuration = inactivityDuration - inactivitySettings.warningDurationMs
    activityBroadcastThrottle = inactivitySettings.activityBroadcastThrottleMs
  }

  const events = ['mousemove', 'keydown', 'scroll', 'touchstart']
  events.forEach((event) => {
    document.addEventListener(event, broadcastMessage('reset'))
  })

  $message
    .asObservable()
    .pipe(debounceTime(activityBroadcastThrottle))
    .subscribe((message) => {
      if (message === 'reset' && !inactivityWarningVisible) {
        initInactivityTimers()
      }
      channel.postMessage(message)
    })

  // Start the initial timer.
  initInactivityTimers()
}

export const startMaxSessionDetection = async () => {
  const { inactivitySettings } = await getPublicConfig()

  // clear timers
  clearTimeout(absoluteTimer)
  clearTimeout(absoluteWarningTimer)

  const storedStartDate = localStorage.getItem('session-start-date')
  const currentTimeMs = new Date().getTime()
  const sessionStartElapsedTimeMs = storedStartDate ? new Date(storedStartDate).getTime() : 0
  const activeSessionTimeMs = currentTimeMs - sessionStartElapsedTimeMs

  // set durations from settings
  if (inactivitySettings) {
    absoluteDuration = inactivitySettings.maxSessionDurationMs
    absoluteWarningDuration = absoluteDuration - inactivitySettings.sessionWarningDurationMs
  }

  // if absolute duration has passed since stored start date
  if (activeSessionTimeMs >= absoluteDuration) {
    return onAbsolute()
  }

  // if absolute duration has not passed since stored start date, set durations based on how much time is left
  if (activeSessionTimeMs < absoluteDuration) {
    absoluteDuration = absoluteDuration - activeSessionTimeMs
    absoluteWarningDuration = absoluteDuration - inactivitySettings.sessionWarningDurationMs
  }

  // if warning duration passed set absoluteWarningDuration so it can be displayed if not seen by the user
  if (absoluteWarningDuration <= 0) {
    absoluteWarningDuration = 0
  }

  // if warning is not seen by user then set timer
  if (!localStorage.getItem('absolute-session-seen')) {
    absoluteWarningTimer = setTimeout(onAbsoluteWarning, absoluteWarningDuration)
  }

  // Set the absolute timer.
  absoluteTimer = setTimeout(onAbsolute, absoluteDuration)
}
